import { createSlice } from '@reduxjs/toolkit'
import { Company } from '@/types/company'
import { Category } from '@/types/category'
import { JoinedGroup } from '@/types/group'

interface MemberState {
  memberInfo: {
    memberId: number
    following: {
      companies: Company[]
      categories: Category[]
    }
    favoriteStoryIds: number[]
    joinedGroups: JoinedGroup[]
  }
  // ログインメッセージ表示中フラグ
  loginMessageShown: boolean
}

const initialState: MemberState = {
  memberInfo: {
    memberId: 0,
    following: {
      companies: [],
      categories: [],
    },
    favoriteStoryIds: [],
    joinedGroups: [],
  },
  loginMessageShown: false,
}

const memberSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    setMemberInfo(state, action) {
      state.memberInfo = {
        ...action.payload,
        joinedGroups: action.payload.joinedGroups ?? state.memberInfo.joinedGroups ?? [],
      }
    },
    updateFavoriteStoryIds(state, action) {
      const { storyId, isAdding } = action.payload
      const favoriteIds = state.memberInfo.favoriteStoryIds || []

      if (isAdding) {
        // Add
        if (!favoriteIds.some((id) => Number(id) === Number(storyId))) {
          state.memberInfo.favoriteStoryIds = [...favoriteIds, Number(storyId)]
        }
      } else {
        // Remove
        state.memberInfo.favoriteStoryIds = favoriteIds.filter(
          (id) => Number(id) !== Number(storyId),
        )
      }
    },
    updateFollowing(state, action) {
      const { item, type, flag } = action.payload

      if (type === 'company') {
        const targetArray = state.memberInfo.following.companies
        if (flag === 'add') {
          // Add
          const exists = targetArray.some((target) => String(target.id) === String(item.id))
          if (!exists) {
            state.memberInfo.following.companies = [...targetArray, item]
          }
        } else if (flag === 'remove') {
          // Remove
          state.memberInfo.following.companies = targetArray.filter(
            (target) => String(target.id) !== String(item.id),
          )
        }
      } else {
        const targetArray = state.memberInfo.following.categories
        if (flag === 'add') {
          // Add
          const exists = targetArray.some((target) => String(target.id) === String(item.id))
          if (!exists) {
            state.memberInfo.following.categories = [...targetArray, item]
          }
        } else if (flag === 'remove') {
          // Remove
          state.memberInfo.following.categories = targetArray.filter(
            (target) => String(target.id) !== String(item.id),
          )
        }
      }
    },
    setLoginMessageShown(state, action) {
      state.loginMessageShown = action.payload
    },
    addJoinedGroup(state, action) {
      const group = action.payload
      const groups = state.memberInfo.joinedGroups ?? []
      const exists = groups.some((g) => g.id === group.id)
      if (!exists) {
        state.memberInfo.joinedGroups = [...groups, group]
      }
    },
    removeJoinedGroup(state, action) {
      const groupId = action.payload
      const groups = state.memberInfo.joinedGroups ?? []
      state.memberInfo.joinedGroups = groups.filter((group) => group.id !== groupId)
    },
  },
})

export const {
  setMemberInfo,
  updateFavoriteStoryIds,
  updateFollowing,
  setLoginMessageShown,
  addJoinedGroup,
  removeJoinedGroup,
} = memberSlice.actions

// Selectors
export const selectMemberInfo = (state: { member: MemberState }) => state.member.memberInfo
export const selectMemberInfoCompanies = (state: { member: MemberState }) =>
  state.member.memberInfo.following.companies
export const selectMemberInfoCategories = (state: { member: MemberState }) =>
  state.member.memberInfo.following.categories
export const selectFavoriteStoryIds = (state: { member: MemberState }) =>
  state.member.memberInfo.favoriteStoryIds
export const selectLoginMessageShown = (state: { member: MemberState }) =>
  state.member.loginMessageShown
export const selectJoinedGroups = (state: { member: MemberState }) =>
  state.member.memberInfo.joinedGroups

export default memberSlice.reducer
